// Отправка событий в счётчики магазина.
//
// Единого формата нет, и это не придирка: dataLayer читает только Google Tag
// Manager, gtag.js кладёт туда же, но свой собственный формат (`arguments`, а не
// объект), а Meta и TikTok про dataLayer не знают вовсе. Поэтому зовём каждый
// транспорт отдельно и только если он действительно загрузился — загрузчики
// ставит сервер в <head> по идентификаторам из настроек магазина.

interface Item {
  id: number | string
  name: string
  price?: number
  quantity?: number
}

export interface TrackPayload {
  value?: number
  currency?: string
  transactionId?: string
  items?: Array<Item>
  // Телефон покупателя, только для покупки: по нему TikTok сопоставляет заказ
  // с аккаунтом, без него Events Manager помечает Purchase как неполный
  phone?: string
}

// Имена событий у Google и Meta разные; TikTok ближе к Meta.
const META_EVENTS: { [key: string]: string } = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase'
}

const TIKTOK_EVENTS: { [key: string]: string } = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'CompletePayment'
}

const CURRENCY = 'UAH'

function win(): any {
  return window as any
}

function gaItems(items?: Array<Item>) {
  return (items || []).map(item => ({
    item_id: String(item.id),
    item_name: item.name,
    price: item.price,
    quantity: item.quantity || 1
  }))
}

// 0XXXXXXXXX и 380XXXXXXXXX → +380XXXXXXXXX: TikTok ждёт номер в E.164
export function e164(phone: string) {
  const digits = phone.replace(/\D/g, '')
  return '+' + (digits.length == 10 && digits[0] == '0' ? '38' + digits : digits)
}

function contents(items?: Array<Item>) {
  return (items || []).map(item => ({
    id: String(item.id),
    quantity: item.quantity || 1,
    item_price: item.price
  }))
}

export function track(name: string, payload: TrackPayload = {}) {
  const w = win()
  const currency = payload.currency || CURRENCY
  const ecommerce: any = {
    currency,
    value: payload.value,
    items: gaItems(payload.items)
  }
  if (payload.transactionId) {
    ecommerce.transaction_id = payload.transactionId
  }

  // GTM. Предыдущий ecommerce гасим: иначе поля прошлого события протекают
  // в следующее — это штатная рекомендация Google, а не перестраховка.
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({ ecommerce: null })
  w.dataLayer.push({ event: name, ecommerce })

  if (typeof w.gtag === 'function') {
    w.gtag('event', name, ecommerce)

    // Конверсия Google Ads — отдельное событие с меткой из настроек магазина.
    // Без метки Ads не знает, какое именно действие засчитать.
    if (name === 'purchase' && w.__adsConversion && w.__adsConversion.sendTo) {
      w.gtag('event', 'conversion', {
        send_to: w.__adsConversion.sendTo,
        value: payload.value,
        currency,
        transaction_id: payload.transactionId
      })
    }
  }

  if (typeof w.fbq === 'function' && META_EVENTS[name]) {
    w.fbq('track', META_EVENTS[name], {
      value: payload.value,
      currency,
      content_type: 'product',
      contents: contents(payload.items)
    })
  }

  if (w.ttq && typeof w.ttq.track === 'function' && TIKTOK_EVENTS[name]) {
    // Хеширует (SHA-256) сам пиксель, до отправки. Сам он номер из формы не
    // находит: ищет поля по name/id, а у нашего их нет
    if (payload.phone) {
      w.ttq.identify({ phone_number: e164(payload.phone) })
    }
    w.ttq.track(TIKTOK_EVENTS[name], {
      value: payload.value,
      currency,
      contents: contents(payload.items)
    })
  }
}

// gtag.js и пиксели сами считают просмотр при загрузке страницы, но смена
// роута в SPA для них не существует. Поэтому первый экран оставляем им, а
// шлём начиная со второго перехода — иначе он посчитается дважды.
let firstRouteSeen = false

export function trackPageView(path: string, title: string) {
  if (!firstRouteSeen) {
    firstRouteSeen = true
    return
  }
  const w = win()
  const page = { page_path: path, page_location: window.location.href, page_title: title }

  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({ event: 'page_view', ...page })

  if (typeof w.gtag === 'function') {
    w.gtag('event', 'page_view', page)
  }
  if (typeof w.fbq === 'function') {
    w.fbq('track', 'PageView')
  }
  if (w.ttq && typeof w.ttq.page === 'function') {
    w.ttq.page()
  }
}
